import { useAppDispatch, useAppSelector } from '~/state';
import { setAvailability, selectAvailability } from '~/state/searchFilters';
import { AvailabilityFilters } from '~/utilities/constants';
import Select, {
  ActionMeta,
  components,
  DropdownIndicatorProps,
  GroupBase,
  OptionProps,
  SingleValueProps,
  ControlProps,
  ValueContainerProps,
  StylesConfig,
} from 'react-select';
import { AvailabilityOption, IsMulti } from '~/utilities/types';
import { useMemo, useRef, useState } from 'react';
import {
  Check,
  ChevronDown,
  ChevronUp,
  CircleCheck,
  Close,
} from '@janeapp/burrito-design-system/icons';
import { Clock } from '@janeapp/burrito-design-system/icons';
import classNames from 'classnames';
import { trackEvent } from '~/hooks';

const styles: StylesConfig<AvailabilityOption, IsMulti> = {
  control: (base, state) => ({
    ...base,
    outline: state.isFocused ? '2px solid #00787d' : 'none',
    outlineOffset: '2px',
    border: '1px solid #BCC6C6',
    boxShadow: 'none',
    '&:hover': {
      border: '1px solid #BCC6C6',
    },
  }),
  // Hide the input field, but keep it for keyboard nav
  // react-select focus + keyboard nav is dependent on the input field
  input: (base) => ({
    ...base,
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    opacity: 0,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? '#f0f0f0' : 'transparent',
    ':active': {
      backgroundColor: 'transparent',
    },
  }),
  indicatorsContainer: () => ({
    background: 'transparent',
  }),
  menu: (base) => ({
    ...base,
  }),
};

export const AvailabilitySelect = () => {
  const dispatch = useAppDispatch();
  const currentAvailabilityFilter = useAppSelector(selectAvailability);
  const inputRef = useRef(null);
  const options: AvailabilityOption[] = [
    { label: 'All', value: AvailabilityFilters.All },
    { label: 'Available today', value: AvailabilityFilters.Today },
    { label: 'Available tomorrow', value: AvailabilityFilters.Tomorrow },
    {
      label: 'Available within a week',
      value: AvailabilityFilters.WithinAWeek,
    },
  ];

  const handleChange = (
    value: AvailabilityOption | null,
    { action }: ActionMeta<AvailabilityOption | null>
  ) => {
    if (action === 'select-option') {
      dispatch(setAvailability(value?.value as AvailabilityFilters));
      trackEvent('mpl_availability_filter_selected', {
        availability: value?.value,
      });
    }
  };

  const Control = ({
    children,
    ...props
  }: ControlProps<AvailabilityOption, IsMulti>) => {
    const isAll =
      (props.selectProps.value as AvailabilityOption)?.value ===
      AvailabilityFilters.All;
    return (
      <components.Control
        {...props}
        className={classNames(
          'availability-select-control', //hack for testing
          'flex !cursor-pointer items-center text-md',
          isAll
            ? '!bg-neutral-20 !text-neutral-100'
            : '!bg-brand-70 !text-white'
        )}
      >
        {children}
      </components.Control>
    );
  };

  const CustomValueContainer = (
    props: ValueContainerProps<AvailabilityOption, IsMulti>
  ) => {
    return (
      <components.ValueContainer {...props}>
        <span className="relative">{props.children}</span>
      </components.ValueContainer>
    );
  };

  const SingleValue = (props: SingleValueProps<AvailabilityOption, false>) => {
    const isAll =
      (props.selectProps.value as AvailabilityOption)?.value ===
      AvailabilityFilters.All;

    const [hovered, setHovered] = useState(false);

    let iconNode;
    if (isAll) {
      iconNode = (
        <Clock
          aria-label="No availability filter applied"
          data-testid="clock-icon"
        />
      );
    } else if (hovered) {
      iconNode = (
        <button
          type="button"
          aria-label="Clear availability filter"
          className="m-0 border-none bg-none p-0 text-white focus:outline-none"
          onMouseDown={(e) => {
            e.preventDefault();
            dispatch(setAvailability(AvailabilityFilters.All));
          }}
        >
          <Close aria-hidden data-testid="close-icon" />
        </button>
      );
    } else {
      iconNode = (
        <CircleCheck
          aria-label="Availability filter applied"
          data-testid="circle-check-icon"
        />
      );
    }

    return (
      <components.SingleValue {...props}>
        <div
          className={classNames(
            'absolute flex items-center gap-2',
            isAll ? 'text-neutral-100' : 'text-white'
          )}
        >
          <div
            className="flex items-center"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
          >
            {iconNode}
          </div>
          <span data-testid="availability-select-label">
            {props.data.value === AvailabilityFilters.All
              ? 'Availability'
              : props.data.label}
          </span>
        </div>
      </components.SingleValue>
    );
  };

  const DropdownIndicator = (
    props: DropdownIndicatorProps<AvailabilityOption, IsMulti>
  ) => {
    const { menuIsOpen } = props.selectProps;
    const isAll =
      (props.selectProps.value as AvailabilityOption)?.value ===
      AvailabilityFilters.All;
    return (
      <components.DropdownIndicator {...props}>
        <div
          className={classNames(
            'border-l pl-2 ',
            isAll
              ? 'border-neutral-30 text-neutral-90'
              : 'border-white text-white opacity-20'
          )}
        >
          {menuIsOpen ? (
            <ChevronUp
              className={classNames(isAll && 'opacity-70', 'h-4 w-4')}
            />
          ) : (
            <ChevronDown
              className={classNames(isAll && 'opacity-70', 'h-4 w-4')}
            />
          )}
        </div>
      </components.DropdownIndicator>
    );
  };

  const Option = (
    props: OptionProps<
      AvailabilityOption,
      IsMulti,
      GroupBase<AvailabilityOption>
    >
  ) => {
    const { data, innerRef, innerProps, isSelected } = props;
    return (
      <components.Option {...props}>
        <div
          ref={innerRef}
          {...innerProps}
          data-testid="availability-option"
          className="flex cursor-pointer items-center justify-between p-0 text-neutral-90"
        >
          {data.label}
          {isSelected && <Check className="text-brand-80" />}
        </div>
      </components.Option>
    );
  };

  const currentValue = useMemo(() => {
    return options.find((option) => option.value === currentAvailabilityFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAvailabilityFilter]);

  return (
    <Select
      ref={inputRef}
      closeMenuOnSelect
      openMenuOnFocus
      blurInputOnSelect
      options={options}
      value={currentValue}
      onChange={handleChange}
      styles={styles}
      aria-label="Filter by availability"
      isSearchable={false}
      className="w-full"
      components={{
        DropdownIndicator,
        ValueContainer: CustomValueContainer,
        SingleValue,
        Control,
        Option,
        IndicatorSeparator: () => null,
      }}
    />
  );
};
